import { useState, useEffect } from "react";

function useLocalStorage(key, initialValue) {
    // Get initial state from localStorage or fallback to initialValue
    const [state, setState] = useState(() => {
        if (typeof window === "undefined") {
            return initialValue; // Return initial value if window is not defined (SSR)
        }
        try {
            const storedValue = window.localStorage.getItem(key);
            return storedValue !== null ? JSON.parse(storedValue) : initialValue;
        } catch (error) {
            console.error("Error reading localStorage key", key, error);
            return initialValue;
        }
    });

    // Sync state with localStorage whenever it changes
    useEffect(() => {
        if (typeof window === "undefined") {
            return; // Do nothing if window is not defined (SSR)
        }
        try {
            window.localStorage.setItem(key, JSON.stringify(state));
        } catch (error) {
            console.error("Error setting localStorage key", key, error);
        }
    }, [key, state]);

    // Handle storage event to sync state across tabs/windows
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === key) {
                try {
                    const newValue = event.newValue ? JSON.parse(event.newValue) : initialValue;
                    setState(newValue);
                } catch (error) {
                    console.error("Error parsing localStorage key", key, error);
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [key, initialValue]);

    return [state, setState];
}

export default useLocalStorage;